import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'

import type { ComposerStore } from '../composerStore'
import { selectEditorForDraft } from '../editorSlice'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'

/**
 * Subscribe to Zustand's `editorSlice.editors` and reconcile Flux's
 * `draft.editorState` to match, via `ComposerActionCreators.updateDraftEditorState`
 * — the same public entry point `EditorWrapper`'s onChange dispatches through
 * when the flag is off, so this reuses `setDraftEditorState`'s side effects
 * (text/YouTube-title sync, comment character count) instead of re-deriving
 * them here.
 *
 * With the flag on, `EditorWrapper` writes to `setEditor` first, making
 * Zustand the entry point; this mirror is what then keeps Flux-internal
 * consumers (thread split/switch, snippet insert, link shorten/unshorten —
 * all the `SlateJsProxy` call sites inside `ComposerStore.ts`) working
 * unchanged, since they still read/mutate `draft.editorState` directly.
 *
 * Reference-diffs each entry against its `EditorRef` *wrapper*, not the
 * unwrapped editor. `setEditor` allocates a fresh `EditorRef` on every call
 * (see `editorSlice.ts`), so the wrapper identity is what actually signals
 * "a write happened" here. The unwrapped editor can't serve that purpose in
 * this direction: this is an identity mirror, so on a real keystroke the
 * editor Zustand was just given IS the same instance Flux already holds —
 * diffing it would never detect a write at all. (`installFluxToEditorStateMirror`
 * diffs the unwrapped editor for a different reason: to decide whether a
 * write is needed, not whether one happened.)
 *
 * @returns An unsubscribe function; further Zustand writes will not reach
 * Flux after it is called.
 */
export function installZustandToEditorStateMirror(
  store: ComposerStore,
): () => void {
  const unsubscribe = store.subscribe((state, prev) => {
    if (state.editors === prev.editors) return
    if (isMirrorWriteInProgress()) return

    const toReconcile = Object.keys(state.editors).filter((draftId) => {
      if (state.editors[draftId] === prev.editors[draftId]) return false
      return selectEditorForDraft(state, draftId) !== undefined
    })

    if (toReconcile.length === 0) return

    runMirrorWrite(() => {
      for (const draftId of toReconcile) {
        const zustandEditor = selectEditorForDraft(state, draftId)
        if (!zustandEditor) continue
        ComposerActionCreators.updateDraftEditorState(draftId, zustandEditor)
      }
    })
  })

  return unsubscribe
}
